import { isEmpty } from 'lodash';
import { generateCollapsedPipelineItems } from './helpers';
import type { FlowNode, MapToNodesOptions, CreateNodesFunction, PipelineItems } from './types';

// Type for mapToNodes function to avoid circular dependency
type MapToNodesFunction = (nodes: FlowNode[], options: MapToNodesOptions) => void;

interface BaseRenderOptions {
  nodes: FlowNode[];
  parentId?: string;
  createNodes: CreateNodesFunction;
  mapToNodes: MapToNodesFunction;
}

interface CollapsedFlowRenderOptions extends BaseRenderOptions {
  item: any;
  options: MapToNodesOptions;
}

export function createCollapsedFlowNodes(renderOptions: CollapsedFlowRenderOptions) {
  const { nodes, item, options, mapToNodes } = renderOptions;

  mapToNodes(nodes, {
    pipelineItems: generateCollapsedPipelineItems(item),
    entities: options.entities,
    aclQueryMode: options.aclQueryMode,
    isRagFlow: options.isRagFlow,
    nodeProps: {
      ...options.nodeProps,
      flow_triggers: item.flow_triggers,
      linksMap: options.linksMap,
      flow: item,
    },
    parentId: `flow-${item.flow_node_id}`,
  });
}

interface RegularFlowRenderOptions extends BaseRenderOptions {
  item: any;
  options: MapToNodesOptions;
}

export function createRegularFlowNodes(renderOptions: RegularFlowRenderOptions) {
  const { nodes, item, options, mapToNodes } = renderOptions;

  mapToNodes(nodes, {
    pipelineItems: item.items,
    entities: options.entities,
    aclQueryMode: options.aclQueryMode,
    isRagFlow: options.isRagFlow,
    nodeProps: {
      ...options.nodeProps,
      linksMap: options.linksMap,
      flow: item,
    },
    parentId: `flow-${item.flow_node_id}`,
  });
}

interface TriggeringFlowRenderOptions extends BaseRenderOptions {
  item: any;
  options: MapToNodesOptions;
}

export function createTriggeringFlowNodes(renderOptions: TriggeringFlowRenderOptions) {
  const { nodes, item, options, mapToNodes } = renderOptions;

  mapToNodes(nodes, {
    ...options,
    pipelineItems: item.triggeringFlowsMap,
    nodeProps: {
      ...options.nodeProps,
      linksMap: options.linksMap,
      flow_triggers: item.flow_triggers,
    },
  });
}

interface RetrieverFlowRenderOptions extends BaseRenderOptions {
  item: any;
  options: MapToNodesOptions;
}

export function createRetrieverFlowNodes(renderOptions: RetrieverFlowRenderOptions) {
  const { nodes, item, options, mapToNodes } = renderOptions;
  const { entities, flowsOrder, keepFlowsOrder, isRagFlow, aclQueryMode, nodeProps } = options;
  const { expandedNodes } = nodeProps;

  item.retriever_flows.forEach((items: any) => {
    mapToNodes(nodes, {
      pipelineItems: items,
      parentId: `node-${item.flow_node_id}`,
      entities,
      flowsOrder,
      keepFlowsOrder,
      isRagFlow,
      aclQueryMode,
      nodeProps: {
        ...nodeProps,
        node_type: item.node_type,
        expandedNodes,
      },
    });
  });
}

interface ResourceRenderOptions extends BaseRenderOptions {
  pipelineItems: PipelineItems;
  flowsOrder?: number[];
  keepFlowsOrder?: boolean;
}

export function createResourceNodes(renderOptions: ResourceRenderOptions) {
  const { nodes, parentId, createNodes, pipelineItems, flowsOrder, keepFlowsOrder } = renderOptions;

  const items = organizeFlowItems(pipelineItems, keepFlowsOrder || false, flowsOrder);

  items.forEach((item) => {
    createNodes(nodes, item, parentId);
  });
}

// Helper function to organize flow items
function organizeFlowItems(
  pipelineItems: PipelineItems,
  keepFlowsOrder: boolean,
  flowsOrder?: number[],
) {
  const triggeredFlows =
    Object.values(pipelineItems).filter((p: any) => !isEmpty(p?.flow_triggers)) || [];

  if (keepFlowsOrder && flowsOrder?.length) {
    return [
      ...flowsOrder.filter((f) => !!pipelineItems[f]).map((f) => pipelineItems[f]),
      ...triggeredFlows,
    ];
  }

  if (flowsOrder?.length) {
    return [
      ...flowsOrder.filter((f) => !!pipelineItems[f]).map((f) => pipelineItems[f]),
      ...triggeredFlows,
    ];
  }

  return Object.values(pipelineItems);
}

// Helper function moved here to avoid circular dependency
export { organizeFlowItems };
